import React, { useEffect, useRef } from 'react';
import { Box, Paper, Typography, CircularProgress, Alert } from '@mui/material';
import { t } from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { CapFirst } from '../../components/capitilizer';
import { setCookie } from '../../helpers/cookies';
import { updateAuthHeaders } from '../../apis/baseApi';
import { styles } from './multi-account.style';
import { AppDispatch } from '@store/types';
import {
  fetchAccounts,
  selectOrganization,
} from '@common/components/authentication/multi-account.thunks';
import { resetState, resetError } from '@common/components/authentication/multi-account.slice';
import { selectMultiAccount } from '@store/selectors';

const MultiAccount: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { accounts: accountList, loading, error, selectingAccount } = useSelector(selectMultiAccount);
  const accounts = Array.isArray(accountList) ? accountList : [];
  const selectedOrgRef = useRef<string | null>(null);

  const getAccounts = () => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get('state') || '';

    if (!state) {
      navigate('/login');
      return;
    }

    dispatch(fetchAccounts(state));
  };

  useEffect(() => {
    getAccounts();

    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  const handleSelectOrganization = async (organizationId: string) => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get('state') || '';

    if (!state) {
      navigate('/login');
      return;
    }
    try {
      selectedOrgRef.current = organizationId;
      const result = await dispatch(selectOrganization({ state, organizationId })).unwrap();
      setCookie('x-access-token', result);
      updateAuthHeaders();
      // Reload the protected application after the account token is committed
      // so its bootstrap performs the authenticated /me check.
      window.location.replace('/');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to select organization:');
      selectedOrgRef.current = null;
    }
  };

  const handleCloseError = () => {
    dispatch(resetError());
  };

  if (loading) {
    return (
      <Box sx={styles.wrapper}>
        <Box sx={styles.subWrapper}>
          <Paper sx={styles.paper}>
            <Box sx={styles.accountsContainer}>
              <img
                src="/assets/images/secondbrain.svg"
                alt="Logo"
                width={40}
                height={40}
                style={{ margin: 10 }}
              />
              <CircularProgress sx={styles.progressCircle} />
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.subWrapper}>
        <Paper sx={styles.paper} variant="outlined">
          <Box sx={styles.accountsContainer}>
            <img
              src="/assets/images/secondbrain.svg"
              alt="Logo"
              width={40}
              height={40}
              style={{ margin: 10 }}
            />
            <CapFirst
              variant="h6"
              fontWeight="bold"
              mb={2}
              content={t('GLOBAL_AVAILABLE_ORGANIZATION', {
                defaultValue: 'Available Organizations',
              })}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={handleCloseError}>
              {t('GLOBAL_ERROR', {
                defaultValue: 'An error occurred while fetching organizations. Please try again.',
              })}
            </Alert>
          )}

          <Box sx={styles.accountsSubContainer}>
            <Box sx={styles.accountsSubC}>
              {accounts.map((account) => (
                <Box
                  key={account.id}
                  onClick={() => handleSelectOrganization(account.id)}
                  sx={{
                    ...styles.accountRow,
                    pointerEvents: selectingAccount ? 'none' : 'auto',
                    opacity: selectingAccount ? 0.7 : 1,
                  }}
                >
                  <Box sx={styles.accountImageWrapper}>
                    <img
                      src={account.logo}
                      width={40}
                      height={40}
                      alt="Organization Logo"
                      loading="lazy"
                    />
                  </Box>
                  <Box sx={styles.accountTextWrapper}>
                    <Typography fontWeight="medium">{account.name}</Typography>
                    <Typography variant="text-sm" color="text.secondary">
                      {account.id}
                    </Typography>
                  </Box>
                  {selectingAccount && selectedOrgRef.current === account.id && (
                    <CircularProgress size={16} sx={{ ml: 'auto' }} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default MultiAccount;
